import React, { Fragment, useState, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { reduxForm, Field, change } from 'redux-form';
import AddIcon from '@material-ui/icons/Add';
import CheckIcon from '@material-ui/icons/Check';
import IconButton from '@material-ui/core/IconButton';
import RadioButtonCheckedIcon from '@material-ui/icons/RadioButtonChecked';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import SubjectIcon from '@material-ui/icons/Subject';
import ThumbsUpDownIcon from '@material-ui/icons/ThumbsUpDown';
import { withTranslation } from 'react-i18next';
import { ThemeProvider } from '@material-ui/core/styles';
import { Button } from 'reactstrap';
import { v4 as uuid } from 'uuid';
import Tooltip from '@material-ui/core/Tooltip';
import SmartHtmlEditor from '../../../../shared/components/form/SmartHtmlEditor';
import { ACTION_NAME, QUESTION_TYPE } from '../../../../shared/utils/constants';
import Select from '../../../../shared/components/form/Select';
import columns from '../../../QuestionDatabase/components/AnswersColumn';
import { showNotificationAutoDestroy } from '../../../../shared/components/ShowNotification';
import TextAreaField from '../../../../shared/components/form/TextAreaField';
import MaterialTable from '../../../../shared/components/material-table';

const Questionnaire = (props) => {
  const {
    subtitle,
    t,
    dispatch,
    onQuestionAdd,
    questionToEdit,
    initialize,
    hideQuestionnaire,
    editable,
  } = props;

  const [question, setQuestion] = useState({
    description: '',
    answers: [],
    type: { value: QUESTION_TYPE.OBJECTIVE },
  });
  const [selectedOption, setSelectedOption] = useState({ description: '' });
  const [clearQuestionEditor, setClearQuestionEditor] = useState(false);
  const [htmlContent, setHtmlContent] = useState(question.description);

  const typesDescriptions = [
    {
      value: QUESTION_TYPE.OBJECTIVE,
      label: t(`question.type-description.${QUESTION_TYPE.OBJECTIVE}`),
      icon: <RadioButtonCheckedIcon />,
    },
    {
      value: QUESTION_TYPE.SELECTION,
      label: t(`question.type-description.${QUESTION_TYPE.SELECTION}`),
      icon: <CheckBoxIcon />,
    },
    {
      value: QUESTION_TYPE.DESCRIPTIVE,
      label: t(`question.type-description.${QUESTION_TYPE.DESCRIPTIVE}`),
      icon: <SubjectIcon />,
    },
    {
      value: QUESTION_TYPE.TRUE_FALSE,
      label: t(`question.type-description.${QUESTION_TYPE.TRUE_FALSE}`),
      icon: <ThumbsUpDownIcon />,
    },
  ];

  const onDescriptionChange = (value) => {
    const updatedQuestion = { ...question };
    updatedQuestion.description = value;
    setQuestion(updatedQuestion);
  };

  const onQuestionTypeChangeHandler = (value) => {
    const updatedQuestion = { ...question };
    if (question.answers) {
      if (value.value === QUESTION_TYPE.SELECTION) {
        updatedQuestion.answers = updatedQuestion.answers.map((item) => ({
          ...item,
          correct: false,
        }));
      }
    }
    updatedQuestion.type = { value: value.value, label: value.label };
    setQuestion(updatedQuestion);
  };

  const onOptionDescriptionChange = (event) => {
    const updatingOption = { ...selectedOption };
    updatingOption.description = event.target.value;
    setSelectedOption(updatingOption);
  };

  const onAddQuestionOption = () => {
    if (!selectedOption.description.trim()) {
      showNotificationAutoDestroy(
        'warning',
        t('warnings.title'),
        t('answers.validation.no-option-description')
      );
    } else {
      const updatedQuestion = { ...question };
      let updatedAnswers = [...updatedQuestion.answers];
      if (selectedOption.hashId) {
        const answersIndex = updatedAnswers.findIndex(
          (item) => item.hashId === selectedOption.hashId
        );
        updatedAnswers[answersIndex] = { ...selectedOption };
      } else {
        updatedAnswers = updatedAnswers.filter(
          (ans) => ans.description !== selectedOption.description
        );
        updatedAnswers.push({
          ...selectedOption,
          hashId: uuid(),
          correct: false,
        });
      }
      setSelectedOption({ description: '' });
      updatedQuestion.answers = updatedAnswers;
      setQuestion(updatedQuestion);
      initialize({
        description: '',
        options: '',
        type: {
          ...typesDescriptions[
            typesDescriptions.findIndex(
              (item) => item.value === question.type.value
            )
          ],
        },
      });
    }
  };

  const updateAnswerHandler = (data, newData) => {
    const updatedQuestion = { ...question };
    if (updatedQuestion.type.value === QUESTION_TYPE.OBJECTIVE) {
      updatedQuestion.answers = updatedQuestion.answers.map((item) => ({
        ...item,
        correct: newData.hashId === item.hashId ? newData.correct : false,
      }));
    } else {
      updatedQuestion.answers = updatedQuestion.answers.map((item) => ({
        ...item,
        trueOrFalse:
          newData.hashId === item.hashId
            ? newData.trueOrFalse
            : item.trueOrFalse,
        correct:
          newData.hashId === item.hashId ? newData.correct : item.correct,
      }));
    }
    setQuestion(updatedQuestion);
  };

  const removeQuestionOptionHandler = (evt, rowData) => {
    const updatedQuestion = { ...question };
    updatedQuestion.answers = updatedQuestion.answers.filter(
      (item) => item.hashId !== rowData.hashId
    );
    setQuestion(updatedQuestion);
  };

  const onQuestionOptionEditHandler = (event, rowData) => {
    const updatedSelectedOtion = { ...rowData, options: rowData.description };
    setSelectedOption(updatedSelectedOtion);
    initialize(updatedSelectedOtion);
  };

  const onAddQuestionClickHandler = () => {
    let message;
    if (!question.description) {
      message = t('question.validation.no-statement-informed');
    } else if (!question.type || !question.type.value) {
      message = t('question.validation.no-type-selected');
    } else if (question.type.value === QUESTION_TYPE.OBJECTIVE) {
      if (question.answers.length <= 1) {
        message = t('question.validation.less-two-options-added');
      }
    } else if (question.type.value === QUESTION_TYPE.SELECTION) {
      if (question.answers.length < 1) {
        message = t('question.validation.no-options-added');
      }
    } else if (question.type.value === QUESTION_TYPE.TRUE_FALSE) {
      if (question.answers.length < 1) {
        message = t('question.validation.no-options-added');
      } else {
        const hasNoAnswered = question.answers.find((op) => !op.trueOrFalse);
        message = hasNoAnswered
          ? t('question.validation.no-trueorfalse-option')
          : '';
      }
    }

    if (message) {
      showNotificationAutoDestroy('warning', t('warnings.title'), message);
    } else {
      onQuestionAdd(question);
      setQuestion({ description: '', answers: [], type: question.type });
      setClearQuestionEditor(true);
    }
  };

  const actionsDisabledByConditional = (propss) => {
    if (propss.actions && propss.actions.length) {
      const copyProps = { ...propss };
      copyProps.actions.find((a) => a.name === ACTION_NAME.EDIT).disabled =
        !editable;
      copyProps.actions.find((a) => a.name === ACTION_NAME.REMOVE).hidden =
        !editable;
    }
  };

  useEffect(() => {
    if (questionToEdit.hashId) {
      questionToEdit.type = typesDescriptions.find(
        (tp) => tp.value === questionToEdit.type.value
      );
      setQuestion({ ...questionToEdit });
      initialize(questionToEdit);
    }
    setHtmlContent(questionToEdit.description);
  }, [questionToEdit]);

  useEffect(() => {
    dispatch(change('CommunicationForm', 'question', question));
    setClearQuestionEditor(false);
  }, [question]);

  return (
    <Fragment>
      {!hideQuestionnaire && (
        <>
          <div className="card__title">
            <h5 className="bold-text">{subtitle}</h5>
          </div>
          <div className="form__form-group">
            <div className="form_form-group-field">
              <Field
                name="description"
                component={SmartHtmlEditor}
                onEditorChange={onDescriptionChange}
                htmlValue={question.description}
                shouldClear={clearQuestionEditor}
                foreignContent={htmlContent}
                showTemplateCommunication={false}
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('question.answer-type')}
            </span>
            <div className="form_form-group-field">
              <Field
                name="type"
                type="text"
                component={Select}
                isClearable={false}
                options={typesDescriptions}
                placeholder={t('question.placeholders.type')}
                onChange={onQuestionTypeChangeHandler}
              />
            </div>
          </div>
        </>
      )}
      {!!question &&
      !!question.type.value &&
      question.type.value !== QUESTION_TYPE.DESCRIPTIVE ? (
        <Fragment>
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('general.options')}
            </span>
            <div className="form__form-group-field col-md-12 col-lg-12">
              <div className="form__form-group-field">
                <Field
                  name="options"
                  type="text"
                  component={TextAreaField}
                  maxLength="250"
                  onChange={onOptionDescriptionChange}
                  disabled={!editable}
                />
              </div>
              <div>
                <Tooltip title={t('general.add-option')}>
                  <IconButton
                    aria-label="add"
                    className="vertical__align__middle ml-2"
                    onClick={onAddQuestionOption}
                    disabled={!editable}
                  >
                    {selectedOption && selectedOption.hashId ? (
                      <CheckIcon />
                    ) : (
                      <AddIcon />
                    )}
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          </div>
          <div className="mb-3 w-100">
            <div className="material-table__wrap">
              <ThemeProvider>
                <MaterialTable
                  isFiltering
                  data={question.answers}
                  columns={columns(t, updateAnswerHandler, question.type)}
                  actionsDisabled
                  onEditClick={onQuestionOptionEditHandler}
                  onRemoveClick={removeQuestionOptionHandler}
                  actionsDisabledByConditional={actionsDisabledByConditional}
                  paging={false}
                  toolbar={false}
                />
              </ThemeProvider>
            </div>
          </div>
        </Fragment>
      ) : null}
      {!hideQuestionnaire && (
        <div className="form__form-group">
          <div className="question-buttons">
            <Button onClick={onAddQuestionClickHandler}>
              {question && question.hashId
                ? t('question.update-question')
                : t('question.add-question')}
            </Button>
          </div>
        </div>
      )}
    </Fragment>
  );
};

Questionnaire.propTypes = {
  t: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  onQuestionAdd: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  subtitle: PropTypes.string,
  questionToEdit: PropTypes.shape(PropTypes.shape).isRequired,
  hideQuestionnaire: PropTypes.bool,
  editable: PropTypes.bool,
};

Questionnaire.defaultProps = {
  subtitle: '',
  hideQuestionnaire: false,
  editable: true,
};

export default reduxForm({
  form: 'Questionnaire',
  keepDirtyOnReinitialize: true,
  enableReinitialize: true,
})(withTranslation('common')(memo(Questionnaire)));
